export interface ShotRect {
  id: string;
  top: number;
  bottom: number;
}

export interface HighlightResult {
  weights: Record<string, number>;
  primaryId: string | null;
}

export interface HighlightOptions {
  quantize?: number;
  primaryMargin?: number;
  minWeight?: number;
}

const DEFAULTS = { quantize: 0.05, primaryMargin: 0.05, minWeight: 0.02 };

/**
 * 按"当前可见的前两张分镜之间的相对占比"算高亮权重。
 * 不碰 DOM：rect 读取在调用方完成，便于单测。
 * 注意：两个权重各自独立量化，和不保证恰好为 1（最多 ~1.05），消费方按强度使用即可。
 */
export function computeShotHighlights(
  rects: ShotRect[],
  bandTop: number,
  bandBottom: number,
  prevPrimaryId: string | null,
  options: HighlightOptions = {},
): HighlightResult {
  const { quantize, primaryMargin, minWeight } = { ...DEFAULTS, ...options };

  // 1. 每个 rect 在 band 内的可见高度
  const visibles = rects
    .map((r) => ({
      id: r.id,
      visible: Math.max(0, Math.min(r.bottom, bandBottom) - Math.max(r.top, bandTop)),
    }))
    .filter((v) => v.visible > 0);

  // 7. 全部不可见
  if (visibles.length === 0) {
    return { weights: {}, primaryId: null };
  }

  // 2. 取可见高度最高的两张（stable sort 保留 rects 原序 → 并列时靠前者优先）
  visibles.sort((a, b) => b.visible - a.visible);
  const top = visibles.slice(0, 2);
  const sum = top.reduce((s, v) => s + v.visible, 0);

  // 3. 归一化
  let w0 = top[0].visible / sum;
  let w1 = top.length > 1 ? top[1].visible / sum : 0;

  // 4. 次高截断（<= 使恰好 2% 的 sliver 也截掉）
  if (w1 <= minWeight) {
    w1 = 0;
    w0 = 1;
  }

  // 5. 量化（减少 setState 频次）
  const q = (w: number): number => Math.round(w / quantize) * quantize;
  const weights: Record<string, number> = {};
  const qw0 = q(w0);
  if (qw0 > 0) weights[top[0].id] = qw0;
  // top[1].id !== top[0].id 防御：分镜 id 实际唯一，但重复 id 不应让较小权重覆盖较大的
  if (w1 > 0 && top[1].id !== top[0].id) {
    const qw1 = q(w1);
    if (qw1 > 0) weights[top[1].id] = qw1;
  }

  // 6. primary = 最大权重（top[0]），带 prevPrimary 滞回
  let primaryId: string | null = top[0].id;
  if (prevPrimaryId && weights[prevPrimaryId] !== undefined) {
    const maxW = weights[top[0].id];
    const prevW = weights[prevPrimaryId];
    if (maxW - prevW <= primaryMargin) {
      primaryId = prevPrimaryId;
    }
  }

  return { weights, primaryId };
}

/** 浅比较两个权重 map（量化后值相等即视为无变化），用于跳过冗余 setState。 */
export function shallowEqualWeights(
  a: Record<string, number>,
  b: Record<string, number>,
): boolean {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

/** 安全探测 color-mix：非浏览器/jsdom 下 CSS 可能不存在，裸访问会 ReferenceError。 */
export function detectColorMix(): boolean {
  return (
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('color', 'color-mix(in srgb, red, blue)')
  );
}

// 模块级缓存一次：结果不变，避免每按钮/每帧重复探测。detectColorMix 自带守卫不会抛。
export const SUPPORTS_COLOR_MIX = detectColorMix();

const clampWeight = (w: number): number =>
  Number.isFinite(w) ? Math.min(1, Math.max(0, w)) : 0;

export interface HighlightStyle {
  background: string;
  color: string;
  fontWeight: number;
  boxShadow: string;
}

/** 按权重 w(0..1) 生成导航按钮的高亮样式片段。w 自带钳制；百分比/alpha 取整避免浮点漂移。 */
export function buildHighlightStyle(
  w: number,
  colorSet: { bg: string; color: string },
  supportsColorMix: boolean = SUPPORTS_COLOR_MIX,
): HighlightStyle {
  const c = clampWeight(w);
  const pct = Math.round(c * 100);        // 整数百分比，避免 "60.00000000000001%"
  const alpha = pct / 100;                // 干净 alpha（最多两位小数）
  const fontWeight = Math.round(600 + 100 * c);
  const boxShadow = `0 1px 4px rgba(0,0,0,${(15 * pct) / 10000})`; // 整数运算→干净阴影 alpha

  if (supportsColorMix) {
    return {
      background: `color-mix(in srgb, var(--primary) ${pct}%, ${colorSet.bg})`,
      color: `color-mix(in srgb, #fff ${pct}%, ${colorSet.color})`,
      fontWeight,
      boxShadow,
    };
  }

  // 降级：不依赖 color-mix 的按比例 rgba overlay；文字粗退化为过半切白。
  // overlay 用 --primary 的 RGB(0,117,222)；仅当 --primary 在运行时被主题覆盖才有色差。
  return {
    background: `linear-gradient(rgba(0,117,222,${alpha}), rgba(0,117,222,${alpha})), ${colorSet.bg}`,
    color: c >= 0.5 ? '#fff' : colorSet.color,
    fontWeight,
    boxShadow,
  };
}
